import { useState, useMemo, Fragment } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { DatesProvider } from "../../../../../../dates/DatesContext";
import { useDates } from "../../../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../../../i18n/useDateFormatter";
import { useModuleEnabled } from "../../../../../../redux/selectors/modules";
import { useLocation, Navigate, createFileRoute } from "@tanstack/react-router";
import { Col, Nav, Row, Table as ReadOnlyTable } from "react-bootstrap";
import useDSPCreativeData from "../../../../../../modules/dsp/hooks/useDSPCreativeData";
import BreadcrumbDates from "../../../../../../components/core/blocks/BreadcrumbDates";
import { DASHBOARD_LINKS } from "../../../../../../utils/dashboardLinks";
import MetricsCard from "../../../../../../components/custom/merchandise/MetricsCard";
import getMetricSpec from "../../../../../../modules/merchandising/utils/metrics";
import { useDSPLineItemDatasetsQuery } from "../../../../../../modules/dsp/hooks/useDSPLineItemDatasetsQuery";
import { snakeCaseToSentenceCase, toTitleCase } from "../../../../../../utils/formatText";
import DetailedMetric from "../../../../../../components/core/blocks/DetailedMetric";
import LargeValue from "../../../../../../components/core/blocks/LargeValue";
import Loading from "../../../../../../components/core/blocks/Loading";
import { DSPCreativesTable } from "../../../../../../modules/dsp/components/DSPCreativesTable";
import DSPLineItemsTable from "../../../../../../modules/dsp/components/DSPLineItemsTable";
import { useAppSelector } from "../../../../../../redux/hooks";
import { GOAL_DEFINITIONS, KPI_DEFINITIONS } from "../../../../../../modules/dsp/utils/constants";
import FlightsTable from "../../../../../../modules/dsp/components/FlightsTable";
import DSPOverlapDashboard from "../../../../../../modules/dsp/components/DSPOverlapDashboard";
import DSPOrderForm from "../../../../../../modules/dsp/forms/DSPOrderForm";
import { Button } from "../../../../../../components/core/basic/Button";
import DSPLineItemForm from "../../../../../../modules/dsp/forms/DSPLineItemForm";
import { useQueryClient } from "@tanstack/react-query";
import DSPGeoDashboard from "../../../../../../modules/dsp/components/DSPGeoDashboard";
import { useDSPOrderConversionTrackingProducts } from "../../../../../../modules/dsp/hooks/useDSPOrderConversionTrackingProducts";
import useDSPSellerInfo from "../../../../../../modules/dsp/hooks/useDSPSellerInfo";
import { Modal } from "../../../../../../components/core/basic/Modal";
import { css } from "../../../../../../../styled-system/css";
import { useSimpleCan } from "../../../../../../config/casl/casl";
import { useDSPCampaignsDatasetsQuery } from "../../../../../../modules/dsp/hooks/useDSPCampaignDatasetsData";
import { getFlightDateRange } from "../../../../../../modules/dsp/utils/flights";

const EMPTY_FLIGHTS = [];

const DSP_TAB_KEYS = ["orderSettings", "configuration", "lineItems", "creatives", "audienceOverlap", "geo"];

const Dashboard = () => {
  const { t } = useTranslation(["dsp"]);
  const dspTabs = useMemo(
    () => [
      { key: "orderSettings", label: t(($) => $.dsp["Order Settings"]) },
      { key: "configuration", label: t(($) => $.dsp.Configuration) },
      { key: "lineItems", label: t(($) => $.dsp["Line Items"]) },
      { key: "creatives", label: t(($) => $.dsp.Creatives) },
      { key: "audienceOverlap", label: t(($) => $.dsp["Audience Overlap"]) },
      { key: "geo", label: t(($) => $.dsp.Geography) },
    ],
    [t],
  );
  const [selectedTab, setSelectedTab] = useState(DSP_TAB_KEYS[0]);
  const [showLineItemCreationModal, setShowLineItemCreationModal] = useState(false);

  const { dspAdvertiser, isLoading: isSellerInfoLoading } = useDSPSellerInfo();
  const channel = useAppSelector((state) => state.channel);
  const hasDSPModule = useModuleEnabled("DSP");
  const userCanCreateDSPEntity = useSimpleCan("update", "DSPEntity");
  const { id: dspOrderId } = Route.useParams();
  const { group, start, end } = useDates();
  const longDate = useDateFormatter("long");
  const queryClient = useQueryClient();

  const { data: conversionTrackingProducts, isLoading: isConversionTrackingProductsDataLoading } =
    useDSPOrderConversionTrackingProducts(dspOrderId);

  const campaignFilter = { id: "campaign_id", value: { op: "eq", value: String(dspOrderId) } };
  const dspCampaignDetailsQuery = useDSPCampaignsDatasetsQuery("campaign_id", true, [campaignFilter]);
  const dspOrgTimelineQuery = useDSPCampaignsDatasetsQuery("org_id,start_date", false, [campaignFilter]);
  const dspAllCampaignsMetadataQuery = useDSPCampaignsDatasetsQuery("campaign_id", true);

  const dspCampaignMetadata = useMemo(
    () => dspCampaignDetailsQuery?.data?.data?.[0] ?? {},
    [dspCampaignDetailsQuery.data],
  );
  const orderName = dspCampaignMetadata.name;
  const advertiserId = dspCampaignMetadata.advertiser_id;

  const orderIdFilter = { id: "campaign_id", value: { op: "eq", value: String(dspOrderId) } };
  const dspLineItemsQuery = useDSPLineItemDatasetsQuery("line_item_id", true, [orderIdFilter]);

  const {
    data: dspCreativeStatsData,
    metadata: dspCreativeMetadata,
    isLoading: isDSPCreativeDataLoading,
  } = useDSPCreativeData("creative_id", true, true, { order_id: dspOrderId });

  const flights = dspCampaignMetadata?.flights ?? EMPTY_FLIGHTS;
  const orderDates = getFlightDateRange(flights);

  const secondaryMetricTileData = useMemo(() => {
    if (!dspCampaignMetadata) {
      return {};
    }

    const processedData = {
      state: snakeCaseToSentenceCase(dspCampaignMetadata.state ?? ""),
      deliveryStatus: snakeCaseToSentenceCase(dspCampaignMetadata.delivery_status ?? ""),
      orderStartDate: orderDates.startDate ? moment(orderDates.startDate).format("MMMM DD, YYYY") : undefined,
      orderEndDate: orderDates.endDate ? moment(orderDates.endDate).format("MMMM DD, YYYY") : undefined,
      budget: flights.reduce((acc, f) => acc + f.budgetAmount, 0),
      optimization: snakeCaseToSentenceCase(dspCampaignMetadata.optimization?.bidStrategy ?? "")?.replace("Kpi", "KPI"),
      biddingPriority: dspCampaignMetadata.optimization?.automateBudgetAllocation,
    };

    return processedData;
  }, [dspCampaignMetadata, flights, orderDates.startDate, orderDates.endDate]);

  const dspLineItemTableData = useMemo(() => {
    return dspLineItemsQuery.data?.data ?? [];
  }, [dspLineItemsQuery.data]);

  const lineItemIds = useMemo(() => {
    return dspLineItemTableData?.map((row) => row.ad_group_id);
  }, [dspLineItemTableData]);

  const dspCreativesData = useMemo(() => {
    const combinedData = dspCreativeStatsData?.map((stats) => {
      const metadata = dspCreativeMetadata?.find((row) => row.creative_id === stats.creative_id) ?? {};

      return { ...metadata, ...stats };
    });
    return combinedData;
  }, [dspCreativeMetadata, dspCreativeStatsData]);

  // Overview stat tile and chart metrics
  const overviewMetrics = useMemo(() => {
    return [
      getMetricSpec("dsp_sales"),
      getMetricSpec("dsp_total_sales"),
      getMetricSpec("dsp_ecpm"),
      getMetricSpec("dsp_impressions"),
      getMetricSpec("dsp_viewable_impressions"),
      getMetricSpec("dsp_viewability"),
      getMetricSpec("dsp_clicks"),
      getMetricSpec("dsp_cost"),
      getMetricSpec("dsp_total_purchases"),
      getMetricSpec("dsp_units"),
      getMetricSpec("dsp_total_units"),
      getMetricSpec("dsp_ctr"),
      getMetricSpec("dsp_total_acos"),
      getMetricSpec("dsp_total_roas"),
      getMetricSpec("dsp_total_detail_page_views"),
      getMetricSpec("dsp_total_detail_page_view_rate"),
      getMetricSpec("dsp_total_add_to_cart"),
      getMetricSpec("dsp_new_to_brand_detail_page_views"),
      getMetricSpec("dsp_new_to_brand_detail_page_views_rate"),
      getMetricSpec("dsp_total_new_to_brand_dpvs"),
      getMetricSpec("dsp_total_new_to_brand_dpv_rate"),
      getMetricSpec("dsp_brand_searches"),
      getMetricSpec("dsp_brand_searches_rate"),
    ];
  }, []);

  const breadCrumbItems = useMemo(() => {
    return [
      {
        name: t(($) => $.dsp["DSP Dashboard"]),
        href: `/user/dsp/${dspAdvertiser?.id}/dashboard`,
        unsorted: true,
        dropdownLinkOptions: DASHBOARD_LINKS,
        dropdownSubtitle: t(($) => $.dsp.Dashboards),
        activeOption: t(($) => $.dsp["DSP Dashboard"]),
      },
      {
        name: orderName,
        href: `/user/dsp/${dspAdvertiser?.id}/orders/${dspOrderId}`,
        dropdownLinkOptions: dspAllCampaignsMetadataQuery.data?.data?.map((campaign) => ({
          ...campaign,
          href: `/user/dsp/${dspAdvertiser?.id}/orders/${campaign.campaign_id}`,
        })),
        searchable: true,
        searchPlaceholderText: t(($) => $.dsp["Search DSP orders..."]),
        dropdownSubtitle: t(($) => $.dsp["DSP Orders"]),
      },
    ];
  }, [dspAdvertiser, dspOrderId, dspAllCampaignsMetadataQuery.data, orderName, t]);

  const renderTabContent = (tab) => {
    switch (tab) {
      case "orderSettings": {
        const { optimization, creation_date_time, name, state, flights, frequencies, delivery_status } =
          dspCampaignMetadata;

        return (
          <Col xs={12} lg={6}>
            <ReadOnlyTable borderless hover>
              <tbody>
                <tr>
                  <th className="d-flex">{t(($) => $.dsp.Name)}</th>
                  <td>{name}</td>
                </tr>
                <tr>
                  <th className="d-flex">{t(($) => $.dsp.State)}</th>
                  <td>{toTitleCase(state ?? "")}</td>
                </tr>
                <tr>
                  <th className="d-flex">{t(($) => $.dsp["Delivery Status"])}</th>
                  <td>{toTitleCase(snakeCaseToSentenceCase(delivery_status) ?? "")}</td>
                </tr>
                <tr>
                  <th className="d-flex">{t(($) => $.dsp["Created On"])}</th>
                  <td>{creation_date_time ? longDate.format(new Date(creation_date_time)) : "—"}</td>
                </tr>
                {optimization && (
                  <tr>
                    <th className="d-flex">{t(($) => $.dsp["Optimization Bidding Priority"])}</th>
                    <td>{snakeCaseToSentenceCase(optimization?.bidStrategy)}</td>
                  </tr>
                )}
                {optimization?.automateBudgetAllocation && (
                  <tr>
                    <th className="d-flex">{t(($) => $.dsp["Optimization Budget Strategy"])}</th>
                    <td>{t(($) => $.dsp["Automate Budget Allocation"])}</td>
                  </tr>
                )}
                <tr>
                  <th className="d-flex">{t(($) => $.dsp["Optimization Goal"])}</th>
                  <td>
                    {toTitleCase(optimization?.goalSetting?.goal ?? "")}
                    <br />
                    <small>{GOAL_DEFINITIONS[optimization?.goalSetting?.goal]}</small>
                  </td>
                </tr>
                <tr>
                  <th className="d-flex">{t(($) => $.dsp["Optimization KPI"])}</th>
                  <td>
                    {snakeCaseToSentenceCase(optimization?.goalSetting?.kpi ?? "")} <br />
                    <small>{KPI_DEFINITIONS[optimization?.goalSetting?.kpi]}</small>
                  </td>
                </tr>
                <tr>
                  <th className="d-flex">{t(($) => $.dsp["Optimization KPI Target"])}</th>
                  <td>{optimization?.goalSetting?.targetKpi}</td>
                </tr>
                <tr>
                  <th className="d-flex">{t(($) => $.dsp.Frequencies)}</th>
                  <td>
                    {Array.isArray(frequencies) &&
                      frequencies.map((frequency, index) => (
                        <Fragment key={index}>
                          <span>{toTitleCase(frequency.frequencyType)}</span>
                          {frequency.maxImpressions && (
                            <span>
                              {t(($) => $.dsp[": no more than {count} impressions a day"], {
                                count: frequency.maxImpressions,
                              })}
                            </span>
                          )}
                        </Fragment>
                      ))}
                  </td>
                </tr>

                <tr>
                  <th className="d-flex">{t(($) => $.dsp.Flights)}</th>
                  <td>
                    <FlightsTable data={flights} />
                  </td>
                </tr>
              </tbody>
            </ReadOnlyTable>
          </Col>
        );
      }
      case "configuration": {
        return (
          <Col xs={12}>
            {isSellerInfoLoading || dspCampaignDetailsQuery.isLoading ?
              <Loading height="300px" />
            : <DSPOrderForm
                initialFormData={{
                  ...dspCampaignMetadata,
                  conversion_tracking_products: conversionTrackingProducts,
                }}
                onSuccessfulSubmit={() => {
                  queryClient.invalidateQueries({
                    predicate: (query) =>
                      ["dspDataSets", "dspLineItemDatasets"].some((key) => query.queryKey[0] === key) ||
                      query.queryKey[0]?.entity === "dspOrdersPaginated",
                  });
                }}
                campaignId={dspOrderId}
                orderEndDate={orderDates.endDate}
                dspAdvertiserId={dspAdvertiser?.advertiser_id}
              />
            }
          </Col>
        );
      }

      case "lineItems": {
        return (
          <>
            <Col xs={12} className="d-flex justify-content-end align-items center mx-auto">
              <Modal
                isOpen={showLineItemCreationModal}
                onOpenChange={(isOpen) => {
                  setShowLineItemCreationModal(isOpen);
                }}
                css={{
                  width: "70vw",
                  maxHeight: "95vh",
                  paddingX: "xlarge",
                }}
              >
                <Row className={css({ marginTop: "large" })}>
                  <Col xs={12}>
                    <DSPLineItemForm
                      initialFormData={{}}
                      createMode={true}
                      onCancel={() => {
                        setShowLineItemCreationModal(false);
                      }}
                      onSuccessfulSubmit={() => {
                        queryClient.invalidateQueries({
                          predicate: (query) =>
                            ["dspLineItemData", "dspLineItemDatasets"].some((key) => query.queryKey[0] === key),
                        });
                        setShowLineItemCreationModal(false);
                      }}
                      campaignId={dspOrderId}
                      advertiserId={advertiserId}
                      orderStartDate={orderDates.startDate}
                      orderEndDate={orderDates.endDate}
                    />
                  </Col>
                </Row>
              </Modal>
              {userCanCreateDSPEntity && (
                <Button
                  onPress={() => {
                    setShowLineItemCreationModal(true);
                  }}
                >
                  {t(($) => $.dsp["Create a New Line Item"])}
                </Button>
              )}
            </Col>
            <Col xs={12}>
              <DSPLineItemsTable orderId={dspOrderId} campaignData={dspCampaignMetadata} />
            </Col>
          </>
        );
      }
      case "creatives": {
        return (
          <Col xs={12}>
            <DSPCreativesTable
              data={dspCreativesData}
              isLoading={isDSPCreativeDataLoading}
              expanderFilters={{ order_id: dspOrderId }}
            />
          </Col>
        );
      }
      case "audienceOverlap": {
        return (
          <Col xs={12}>
            <DSPOverlapDashboard lineItemIds={lineItemIds} />
          </Col>
        );
      }
      case "geo": {
        return (
          <Col xs={12}>
            <DSPGeoDashboard orderId={dspOrderId} />
          </Col>
        );
      }

      default: {
        return null;
      }
    }
  };

  if (!hasDSPModule || channel.currentChannel !== "amazon") {
    return <Navigate to="/user/dashboard" replace />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row className="pb-5">
        <Col xs={12} className="px-5">
          <MetricsCard
            id="dspOverviewDashboard"
            title={orderName}
            initialSelectedMetric={["dsp_sales", "dsp_ecpm", "dsp_impressions", "dsp_total_purchases", "dsp_clicks"]}
            totals={{
              data: dspCampaignDetailsQuery.data?.data?.[0] ?? {},
              loading: isSellerInfoLoading || dspCampaignDetailsQuery.isLoading,
            }}
            timeline={{
              data: dspOrgTimelineQuery.data?.data ?? [],
              loading: isSellerInfoLoading || dspOrgTimelineQuery.isLoading,
            }}
            metrics={overviewMetrics}
            group={group}
            showMetricsSelector={true}
            context={"dspCampaign"}
            byline={
              <>
                {longDate.format(start.toDate())}
                {" - "}
                {longDate.format(end.toDate())}
              </>
            }
          >
            {/* Metadata Stat Tiles */}
            <Row className="pt-5">
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue center name={t(($) => $.dsp.State)} value={secondaryMetricTileData?.state} />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue
                  center
                  name={t(($) => $.dsp["Delivery Status"])}
                  value={secondaryMetricTileData?.deliveryStatus}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue
                  center
                  name={t(($) => $.dsp["Start Date"])}
                  value={secondaryMetricTileData?.orderStartDate}
                />
              </Col>

              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue center name={t(($) => $.dsp["End Date"])} value={secondaryMetricTileData?.orderEndDate} />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  metric={{
                    id: "orderBudget",
                    name: t(($) => $.dsp.Budget),
                    format: "currency",
                    decimalPlaces: 0,
                    accessor: (data) => data.value,
                  }}
                  data={{ value: secondaryMetricTileData?.budget }}
                />
              </Col>

              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue center name={t(($) => $.dsp.Optimization)} value={secondaryMetricTileData?.optimization} />
              </Col>
            </Row>

            {/* Line Items Table  */}
            <Row>
              <Col xs={12} className="mt-5 ph-5 mb-5 border-bottom fs-standard">
                <Nav
                  variant="underline"
                  onSelect={(tab) => {
                    return setSelectedTab(tab);
                  }}
                  activeKey={selectedTab}
                >
                  {dspTabs
                    .map((entry) => ({
                      label: entry.label,
                      key: entry.key,
                    }))

                    ?.map(({ key, label }) => {
                      return (
                        <Nav.Item key={key}>
                          <Nav.Link eventKey={key} style={{ whiteSpace: "nowrap" }}>
                            {label}
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                </Nav>
              </Col>
            </Row>
            <Row>
              {isSellerInfoLoading || dspLineItemsQuery.isLoading || isConversionTrackingProductsDataLoading ?
                <Col xs={12}>
                  <Loading height="300px" />
                </Col>
              : <Col xs={12}>{renderTabContent(selectedTab)}</Col>}
            </Row>
          </MetricsCard>
        </Col>
      </Row>
    </div>
  );
};

const DSPOrderDetails = () => {
  const searchStr = useLocation({ select: (location) => location.searchStr });
  const urlParams = new URLSearchParams(searchStr);

  return (
    <DatesProvider
      period={urlParams.get("period")}
      group={urlParams.get("group")}
      compare={urlParams.get("compare")}
      start={urlParams.get("start")}
      end={urlParams.get("end")}
    >
      <Dashboard />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/dsp/$dspAdvertiser/orders/$id")({
  component: DSPOrderDetails,
  staticData: { application: "Advertising" },
});
